import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import axios from "axios";

import "../../Styles/UserAuthStyles/AuthModal.css";
import "../../Styles/UserAuthStyles/Login.css";
import { useUser } from "../../Context/UserContext";
import { api, setTokens } from "../../api";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignup: () => void;
}

function LoginModal({ isOpen, onClose, onSwitchToSignup }: LoginModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { fetchUser } = useUser();

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleLoginEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const res = await api.post("/api/login", {
        loginMethod: "email",
        email,
        password,
      });

      // Store tokens from response
      if (res.data.accessToken && res.data.refreshToken) {
        setTokens(res.data.accessToken, res.data.refreshToken);
      }

      await fetchUser();
      onClose();
      navigate("/dashboard");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const status = err.response.status;
        const message: string = err.response.data?.message ?? "";

        if (status === 403) {
          toast.info("Please verify your email first. We've sent a new verification link to your inbox.");
        } else if (status === 400 || status === 401) {
          toast.error(message || "Invalid email or password.");
        } else {
          toast.error(message || "An unexpected error occurred. Please try again.");
        }
      } else {
        console.error(err);
        toast.error("Unable to connect. Please check your internet connection and try again.");
      }
    }
  };

  interface GoogleJwtPayload {
    name?: string;
    email?: string;
  }

  const handleLoginGoogle = async (googleCredential: string) => {
    try {
      const decoded = jwtDecode<GoogleJwtPayload>(googleCredential);

      const res = await api.post("/api/login", {
        loginMethod: "google",
        email: decoded.email,
        googleSecret: googleCredential,
      });

      // Store tokens from response
      if (res.data.accessToken && res.data.refreshToken) {
        setTokens(res.data.accessToken, res.data.refreshToken);
      }

      await fetchUser();
      onClose();
      navigate("/dashboard");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const message: string = err.response.data?.message ?? "";
        toast.error(message || "An unexpected error occurred. Please try again.");
      } else {
        console.error(err);
        toast.error("Unable to connect. Please check your internet connection and try again.");
      }
    }
  };

  const handleForgotPassword = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    if (!email) {
      toast.info("Enter Email");
      return;
    }
    try {
      await api.post("/api/forgot-password", { email });
      toast.success("A password reset link has been sent to your email.");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const message: string = err.response.data?.message ?? "";
        toast.error(message || "An unexpected error occurred. Please try again.");
      } else {
        console.error(err);
        toast.error("Unable to connect. Please check your internet connection and try again.");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="login-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          type="button"
          className="auth-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &#x2715;
        </button>

        {/* Left hero pane — identical to Login.tsx */}
        <div className="login-hero">
          <h1 className="login-hero-title">
            Sign documents without the friction.
          </h1>
          <p className="login-hero-subtitle">
            Send, track, and complete agreements in minutes. Your dashboard
            keeps every signature in one clean, organized view.
          </p>

          <ul className="login-hero-list">
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Track status</strong> — see who&apos;s viewed and
                signed.
              </span>
            </li>
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Reusable templates</strong> for your most common
                contracts.
              </span>
            </li>
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Secure, compliant signing</strong> your clients can
                trust.
              </span>
            </li>
          </ul>

          <div className="login-hero-footnote">
            You&apos;re moments away from your first signed document.
          </div>
        </div>

        {/* Right form pane — identical to Login.tsx */}
        <div className="login-form-pane">
          <header className="login-header">
            <h2 className="login-title">Welcome back</h2>
            <p className="login-subtitle">
              Sign in to access your dashboard and documents.
            </p>
          </header>

          <form className="login-form" onSubmit={handleLoginEmail}>
            <label className="login-field">
              <span className="login-label">Email</span>
              <input
                type="email"
                id="modal-login-email"
                name="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>

            <label className="login-field">
              <span className="login-label">Password</span>
              <input
                type="password"
                id="modal-login-password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            <div className="login-actions-row">
              <button
                type="button"
                className="login-link"
                onClick={handleForgotPassword}
              >
                Forgot password?
              </button>

              <button type="submit" className="login-primary-button">
                <span className="login-primary-text">Sign in</span>
              </button>
            </div>

            <div className="login-divider">
              <span>or continue with</span>
            </div>

            <div className="login-google">
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  const token = credentialResponse.credential;
                  if (token) handleLoginGoogle(token);
                }}
                onError={() => {
                  toast.error("Google Login Failed");
                }}
              />
            </div>
          </form>

          <p className="login-register">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              className="login-link login-link-strong"
              onClick={onSwitchToSignup}
            >
              Create an account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginModal;
